/**
 * Social reel destinations + free motion styles for Photo Studio.
 */

export interface ReelPlatform {
  id: string;
  label: string;
  shortLabel: string;
  width: number;
  height: number;
  /** Bottom safe inset as fraction of height (UI chrome). */
  safeBottom: number;
  /** Top safe inset as fraction of height. */
  safeTop: number;
  defaultDurationSec: number;
  tip: string;
  /** Preferred default background preset id from SOCIAL_BG_PRESETS */
  defaultBgId: string;
}

export const REEL_PLATFORMS: ReelPlatform[] = [
  {
    id: 'instagram',
    label: 'Instagram Reels',
    shortLabel: 'Instagram',
    width: 1080,
    height: 1920,
    safeBottom: 0.14,
    safeTop: 0.08,
    defaultDurationSec: 5,
    tip: 'Keep face mid-frame; avoid the bottom UI bar.',
    defaultBgId: 'ig-purple',
  },
  {
    id: 'tiktok',
    label: 'TikTok',
    shortLabel: 'TikTok',
    width: 1080,
    height: 1920,
    safeBottom: 0.16,
    safeTop: 0.1,
    defaultDurationSec: 5,
    tip: 'Leave space on the right for captions and buttons.',
    defaultBgId: 'navy',
  },
  {
    id: 'shorts',
    label: 'YouTube Shorts',
    shortLabel: 'Shorts',
    width: 1080,
    height: 1920,
    safeBottom: 0.12,
    safeTop: 0.1,
    defaultDurationSec: 8,
    tip: 'Clean center crop works best for Shorts.',
    defaultBgId: 'white',
  },
  {
    id: 'linkedin',
    label: 'LinkedIn',
    shortLabel: 'LinkedIn',
    width: 1080,
    height: 1350,
    safeBottom: 0.1,
    safeTop: 0.06,
    defaultDurationSec: 5,
    tip: 'Professional framing — suit + soft background recommended.',
    defaultBgId: 'linkedin-blue',
  },
];

export type MotionStyleId = 'push-in' | 'drift' | 'ai-breathe';

export interface MotionStyle {
  id: MotionStyleId;
  label: string;
  description: string;
  costCredits: number;
  /** Local Ken Burns params when cost is 0 */
  startScale?: number;
  endScale?: number;
  panX?: number;
  panY?: number;
}

export const MOTION_STYLES: MotionStyle[] = [
  {
    id: 'push-in',
    label: 'Push-in',
    description: 'Gentle zoom toward the subject',
    costCredits: 0,
    startScale: 1.04,
    endScale: 1.2,
    panX: 0.03,
    panY: -0.05,
  },
  {
    id: 'drift',
    label: 'Drift',
    description: 'Slow pan with light zoom',
    costCredits: 0,
    startScale: 1.08,
    endScale: 1.16,
    panX: 0.08,
    panY: -0.03,
  },
  {
    id: 'ai-breathe',
    label: 'AI motion',
    description: 'Natural portrait motion (cloud)',
    costCredits: 5,
  },
];

export function getReelPlatform(id: string): ReelPlatform {
  return REEL_PLATFORMS.find((p) => p.id === id) || REEL_PLATFORMS[0];
}

export function getMotionStyle(id: MotionStyleId): MotionStyle {
  return MOTION_STYLES.find((m) => m.id === id) || MOTION_STYLES[0];
}
